<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePasskey, type PasskeyCredential } from '@/composables/usePasskey'
import { useAuthStore } from '@/stores/auth'
import { BaseButton, BaseInput, BaseModalClose } from '@/components/base'

interface Props {
  isOpen: boolean
  promptMode?: boolean
}

interface Emits {
  close: []
}

const props = withDefaults(defineProps<Props>(), {
  promptMode: false,
})
const emit = defineEmits<Emits>()

const { t } = useI18n()
const authStore = useAuthStore()
const { isLoading, error, isSupported, registerPasskey, getCredentials, deleteCredential, loginWithPasskey } =
  usePasskey()

const credentials = ref<PasskeyCredential[]>([])
const successMessage = ref('')
const deletingId = ref<string | null>(null)
const newAlias = ref('')
const currentView = ref<'prompt' | 'manage'>('manage')
const isPromptMode = ref(props.promptMode)

// Delete confirmation state
const pendingDeleteCred = ref<PasskeyCredential | null>(null)
const showDeleteConfirm = ref(false)
const isAuthenticating = ref(false)
const authError = ref('')

watch(
  () => props.isOpen,
  async (open) => {
    if (open) {
      successMessage.value = ''
      error.value = ''
      isPromptMode.value = props.promptMode
      if (props.promptMode) {
        currentView.value = 'prompt'
      } else {
        currentView.value = 'manage'
        await loadCredentials()
      }
    }
  },
)

const loadCredentials = async () => {
  const result = await getCredentials()
  if (result.success) {
    credentials.value = result.credentials
  }
}

const handleSetupNow = async () => {
  currentView.value = 'manage'
  await loadCredentials()
}

const handleSkip = () => {
  const expires = new Date()
  expires.setDate(expires.getDate() + 30)
  document.cookie = `passkey_setup_skipped=1; expires=${expires.toUTCString()}; path=/`
  emit('close')
}

const handleAddPasskey = async () => {
  successMessage.value = ''
  error.value = ''

  const result = await registerPasskey(newAlias.value.trim())
  if (result.success) {
    newAlias.value = ''
    successMessage.value = t('passkey.registrationSuccess')
    await loadCredentials()
    setTimeout(() => {
      successMessage.value = ''
      isPromptMode.value = false
    }, 3000)
  }
}

const requestDelete = (cred: PasskeyCredential) => {
  pendingDeleteCred.value = cred
  authError.value = ''
  showDeleteConfirm.value = true
}

const cancelDelete = () => {
  showDeleteConfirm.value = false
  pendingDeleteCred.value = null
  authError.value = ''
}

const confirmDelete = async () => {
  if (!pendingDeleteCred.value) return

  authError.value = ''
  isAuthenticating.value = true

  // Re-authenticate with passkey before deleting
  const authResult = await loginWithPasskey(authStore.user?.email)
  isAuthenticating.value = false

  if (!authResult.success) {
    if (!authResult.cancelled) {
      authError.value = t('passkey.authFailed')
    }
    return
  }

  const credId = pendingDeleteCred.value.id
  showDeleteConfirm.value = false
  pendingDeleteCred.value = null

  successMessage.value = ''
  error.value = ''
  deletingId.value = credId

  const result = await deleteCredential(credId)
  deletingId.value = null

  if (result.success) {
    credentials.value = credentials.value.filter((c) => c.id !== credId)

    const skipped = document.cookie.split('; ').some((c) => c.startsWith('passkey_setup_skipped='))
    if (!skipped && isSupported() && credentials.value.length === 0) {
      isPromptMode.value = true
      currentView.value = 'prompt'
    } else {
      successMessage.value = t('passkey.deleteSuccess')
      setTimeout(() => {
        successMessage.value = ''
      }, 3000)
    }
  }
}

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  })
}

const handleClose = () => {
  emit('close')
}
</script>

<template>
  <!-- Modal Backdrop -->
  <div
    v-if="isOpen"
    class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
  >
    <!-- ===== PROMPT VIEW ===== -->
    <div
      v-if="currentView === 'prompt'"
      class="bg-white rounded-2xl shadow-xl w-full overflow-hidden"
      style="max-width: 680px"
      @click.stop
    >
      <div class="px-10 py-10 flex flex-col sm:flex-row items-center gap-8">
        <!-- Left: Text content -->
        <div class="flex-1 min-w-0">
          <!-- Brand -->
          <div class="flex items-center gap-2 mb-8">
            <img src="/Flixorder_Web_icon-300x300.png" alt="Flixorder" class="h-9 w-9 rounded" />
            <span class="text-xl font-bold text-gray-900">Flixorder</span>
          </div>

          <h2 class="text-2xl font-bold text-gray-900 mb-3">
            {{ t('passkey.setupTitle') }}
          </h2>
          <p class="text-sm text-gray-600 leading-relaxed mb-2">
            {{ t('passkey.setupDesc1') }}
          </p>
          <p class="text-sm text-gray-600 leading-relaxed mb-7">
            {{ t('passkey.setupDesc2') }}
          </p>

          <div
            class="inline-flex flex-col sm:flex-row gap-3"
          >
            <BaseButton
              @click="handleSetupNow"
            >
              {{ t('passkey.setupNow') }}
            </BaseButton>
            <button
              @click="handleSkip"
              class="px-6 py-2.5 border border-primary-400 text-primary-500 hover:bg-primary-50 font-semibold rounded-lg transition-colors text-sm"
            >
              {{ t('passkey.skipForNow') }}
            </button>
          </div>

        </div>

        <!-- Right: Security icons -->
        <div class="flex-shrink-0 flex flex-col items-center justify-center gap-8">
          <!-- Fingerprint icon -->
          <svg
            class="w-20 h-20 text-gray-800"
            viewBox="0 0 80 80"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M28 40c0-6.627 5.373-12 12-12s12 5.373 12 12c0 5-2 10-4 14" />
            <path d="M22 34c0-9.941 8.059-18 18-18s18 8.059 18 18c0 6-2 13-5 19" />
            <path d="M16 28c0-13.255 10.745-24 24-24s24 10.745 24 24c0 7-2 16-6 24" />
            <circle cx="40" cy="40" r="2.5" fill="currentColor" />
            <path d="M40 43v7" />
          </svg>

          <!-- Face ID + PIN row -->
          <div class="flex gap-8 items-center">
            <!-- Face ID -->
            <svg
              class="w-14 h-14 text-gray-700"
              viewBox="0 0 56 56"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <!-- Corner brackets -->
              <path d="M4 16V8a4 4 0 014-4h8" />
              <path d="M40 4h8a4 4 0 014 4v8" />
              <path d="M52 40v8a4 4 0 01-4 4h-8" />
              <path d="M16 52H8a4 4 0 01-4-4v-8" />
              <!-- Eyes -->
              <circle cx="21" cy="24" r="2" fill="currentColor" stroke="none" />
              <circle cx="35" cy="24" r="2" fill="currentColor" stroke="none" />
              <!-- Smile -->
              <path d="M21 35c0 3.866 3.134 7 7 7s7-3.134 7-7" />
            </svg>

            <!-- PIN / grid -->
            <svg
              class="w-14 h-14 text-gray-700"
              viewBox="0 0 56 56"
              fill="none"
            >
              <circle cx="18" cy="16" r="3.5" fill="currentColor" />
              <circle cx="28" cy="16" r="3.5" fill="currentColor" />
              <circle cx="38" cy="16" r="3.5" fill="currentColor" />
              <circle cx="18" cy="28" r="3.5" fill="currentColor" />
              <circle cx="28" cy="28" r="3.5" fill="currentColor" />
              <circle cx="38" cy="28" r="3.5" fill="currentColor" />
              <circle cx="18" cy="40" r="3.5" fill="currentColor" />
              <circle cx="28" cy="40" r="3.5" fill="currentColor" />
              <circle cx="38" cy="40" r="3.5" fill="currentColor" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== MANAGE VIEW ===== -->
    <div
      v-else
      class="relative bg-white rounded-xl shadow-xl max-w-xl w-full"
      @click.stop
    >
      <!-- Modal Header -->
      <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div class="flex items-center gap-2">
          <svg class="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15.75 1.5a6.75 6.75 0 0 0-6.651 7.906c.067.39-.032.717-.221.906l-6.5 6.499a3 3 0 0 0-.878 2.121v2.818c0 .414.336.75.75.75H6a.75.75 0 0 0 .75-.75v-1.5h1.5A.75.75 0 0 0 9 19.5V18h1.5a.75.75 0 0 0 .53-.22l2.658-2.658c.19-.189.517-.288.906-.22A6.75 6.75 0 1 0 15.75 1.5Zm0 3a.75.75 0 0 0 0 1.5A2.25 2.25 0 0 1 18 8.25a.75.75 0 0 0 1.5 0 3.75 3.75 0 0 0-3.75-3.75Z"
            />
          </svg>
          <h2 class="text-lg font-semibold text-gray-900">{{ t('passkey.title') }}</h2>
        </div>
        <!-- Back button when navigated from prompt view, close button otherwise -->
        <button
          v-if="isPromptMode"
          @click="currentView = 'prompt'"
          class="flex items-center gap-1 text-gray-500 hover:text-gray-700 transition-colors text-sm font-medium"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
          {{ t('common.back') }}
        </button>
        <BaseModalClose v-else @click="handleClose" />
      </div>

      <!-- Modal Body -->
      <div class="px-6 py-4">
        <!-- Browser not supported -->
        <div
          v-if="!isSupported()"
          class="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm"
        >
          {{ t('passkey.notSupported') }}
        </div>

        <!-- Success Message -->
        <div
          v-if="successMessage"
          class="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm"
        >
          {{ successMessage }}
        </div>

        <!-- Error Message -->
        <div
          v-if="error"
          class="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm"
        >
          {{ error }}
        </div>

        <!-- Loading list -->
        <div v-if="isLoading && credentials.length === 0" class="py-6 flex justify-center">
          <svg class="animate-spin h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>

        <!-- Credentials list -->
        <div v-else>
          <p v-if="credentials.length === 0" class="text-sm text-gray-500 py-4 text-center">
            {{ t('passkey.noPasskeys') }}
          </p>

          <ul v-else class="divide-y divide-gray-100 mb-4">
            <li
              v-for="cred in credentials"
              :key="cred.id"
              class="py-3 flex items-start justify-between gap-3"
            >
              <div class="min-w-0 flex-1">
                <!-- Name + status badge -->
                <div class="flex items-center gap-2 flex-wrap">
                  <p class="text-sm font-medium text-gray-900 truncate">
                    {{ cred.alias || t('passkey.unnamedPasskey') }}
                  </p>
                  <span
                    :class="cred.is_enabled
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'"
                    class="text-xs font-medium px-1.5 py-0.5 rounded"
                  >
                    {{ cred.is_enabled ? t('passkey.enabled') : t('passkey.disabled') }}
                  </span>
                </div>

                <!-- Detail grid -->
                <dl class="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-gray-500">
                  <div v-if="cred.device_type" class="flex gap-1">
                    <dt class="font-medium text-gray-600">{{ t('passkey.deviceType') }}:</dt>
                    <dd class="capitalize">{{ cred.device_type }}</dd>
                  </div>
                  <div v-if="cred.transports?.length" class="flex gap-1">
                    <dt class="font-medium text-gray-600">{{ t('passkey.transports') }}:</dt>
                    <dd>{{ cred.transports.join(', ') }}</dd>
                  </div>
                  <div class="flex gap-1">
                    <dt class="font-medium text-gray-600">{{ t('passkey.registeredAt') }}:</dt>
                    <dd>{{ formatDate(cred.created_at) }}</dd>
                  </div>
                </dl>
              </div>

              <button
                @click="requestDelete(cred)"
                :disabled="deletingId === cred.id"
                class="flex-shrink-0 text-red-500 hover:text-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed mt-0.5"
                :title="t('passkey.delete')"
              >
                <svg
                  v-if="deletingId !== cred.id"
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                <svg
                  v-else
                  class="animate-spin w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </button>
            </li>
          </ul>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="px-6 py-4 border-t border-gray-200 flex items-center justify-between gap-3">
        <BaseInput
          v-if="isSupported()"
          v-model="newAlias"
          type="text"
          :placeholder="t('passkey.aliasPlaceholder')"
          :disabled="isLoading"
          class="flex-1 min-w-0"
        />
        <BaseButton
          v-if="isSupported()"
          @click="handleAddPasskey"
          :disabled="isLoading"
        >
          <svg
            v-if="!isLoading"
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
          <svg v-else class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span>{{ isLoading ? t('passkey.adding') : t('passkey.add') }}</span>
        </BaseButton>
        <span v-else></span>

        <BaseButton
          v-if="!isPromptMode"
          @click="handleClose"
          variant="secondary"
        >
          {{ t('common.close') }}
        </BaseButton>
      </div>

      <!-- ===== DELETE CONFIRMATION OVERLAY ===== -->
      <div
        v-if="showDeleteConfirm"
        class="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 rounded-xl z-10"
      @click.stop
    >
      <div class="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
        <!-- Icon -->
        <div class="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mx-auto mb-4">
          <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
        </div>

        <h3 class="text-base font-semibold text-gray-900 text-center mb-1">
          {{ t('passkey.deleteConfirm') }}
        </h3>
        <p class="text-sm text-gray-500 text-center mb-2">
          <span class="font-medium text-gray-700">{{ pendingDeleteCred?.alias || t('passkey.unnamedPasskey') }}</span>
        </p>
        <p class="text-xs text-gray-500 text-center mb-5">
          {{ t('passkey.deleteAuthPrompt') }}
        </p>

        <!-- Auth error -->
        <div v-if="authError" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs text-center">
          {{ authError }}
        </div>

        <div class="flex gap-3">
          <BaseButton
            @click="cancelDelete"
            :disabled="isAuthenticating"
            variant="secondary"
            class="flex-1"
          >
            {{ t('common.cancel') }}
          </BaseButton>
          <BaseButton
            @click="confirmDelete"
            :disabled="isAuthenticating"
            variant="danger"
            class="flex-1"
          >
            <svg v-if="isAuthenticating" class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M15.75 1.5a6.75 6.75 0 0 0-6.651 7.906c.067.39-.032.717-.221.906l-6.5 6.499a3 3 0 0 0-.878 2.121v2.818c0 .414.336.75.75.75H6a.75.75 0 0 0 .75-.75v-1.5h1.5A.75.75 0 0 0 9 19.5V18h1.5a.75.75 0 0 0 .53-.22l2.658-2.658c.19-.189.517-.288.906-.22A6.75 6.75 0 1 0 15.75 1.5Z" />
            </svg>
            <span>{{ isAuthenticating ? t('passkey.authenticating') : t('passkey.authenticateAndDelete') }}</span>
          </BaseButton>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>
